import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const PDF_SETTINGS = {
  orientation: "landscape",
  unit: "mm",
  format: "a4",
};

const DEFAULT_MARGIN = 15;
const MIN_PAGE_REMAINING_SPACE = 60;

// Column widths are defined in mm; they will be scaled to fit the page width.
const BASE_COLUMN_WIDTHS = [
  55, // School
  50, // Program
  15, // Remark
  22, // Preliminary lowest
  22, // Preliminary average
  22, // Final lowest
  22, // Final average
];

// Table columns (without municipality since it's a group header)
const TABLE_COLUMNS = [
  { header: "Skola", dataKey: "school" },
  { header: "Program", dataKey: "program" },
  { header: "Anm.", dataKey: "remark" },
  { header: "Prel. Lägsta", dataKey: "preliminary_lowest_merit" },
  { header: "Prel. Medel", dataKey: "preliminary_average_merit" },
  { header: "Slut. Lägsta", dataKey: "final_lowest_merit" },
  { header: "Slut. Medel", dataKey: "final_average_merit" },
];

function createPageMunicipalityTracker() {
  const pageToMunicipalities = {};

  const addMunicipalityToPage = (pageNum, municipality) => {
    if (!pageToMunicipalities[pageNum]) {
      pageToMunicipalities[pageNum] = [];
    }
    if (!pageToMunicipalities[pageNum].includes(municipality)) {
      pageToMunicipalities[pageNum].push(municipality);
    }
  };

  return { pageToMunicipalities, addMunicipalityToPage };
}

function getScaledColumnWidths(availableTableWidth, baseColumnWidths) {
  // Scale the base widths so the table spans the full available width.
  // We round to one decimal to avoid overly precise widths in jsPDF, and then
  // compensate for any rounding error by adjusting the last column so that
  // the summed widths match `availableTableWidth` exactly.
  const totalBaseWidth = baseColumnWidths.reduce((sum, w) => sum + w, 0);
  const widthScale =
    totalBaseWidth > 0 ? availableTableWidth / totalBaseWidth : 1;
  const scaledColumnWidths = baseColumnWidths.map(
    (w) => Math.round(w * widthScale * 10) / 10
  );
  const scaledSum = scaledColumnWidths.reduce((sum, w) => sum + w, 0);
  const roundingDiff = Math.round((availableTableWidth - scaledSum) * 10) / 10;
  scaledColumnWidths[scaledColumnWidths.length - 1] += roundingDiff;
  return scaledColumnWidths;
}

function addMainHeader(doc, { margin, yPosition, activeFilters }) {
  const year = activeFilters.year || "Alla år";
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text(`Meritvärden ${year}`, margin, yPosition);
  yPosition += 10;

  if (activeFilters.school) {
    doc.setFontSize(11);
    doc.setFont("helvetica", "italic");
    doc.text(`Skola: ${activeFilters.school}`, margin, yPosition);
    yPosition += 6;
  }

  if (activeFilters.program) {
    doc.setFontSize(11);
    doc.setFont("helvetica", "italic");
    doc.text(`Program: ${activeFilters.program}`, margin, yPosition);
    yPosition += 6;
  }

  return yPosition + 4;
}

function drawMunicipalityHeader(doc, { municipality, margin, yPosition }) {
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(51, 51, 51); // Dark gray
  doc.text(municipality, margin, yPosition);
  doc.setTextColor(0, 0, 0); // Reset to black
  return yPosition + 6;
}

function mapMeritTableData(municipalityData) {
  return municipalityData.map((row) => ({
    school: row.school || "-",
    program: row.program || "-",
    remark: row.remark || "-",
    preliminary_lowest_merit: formatMeritValue(row.preliminary_lowest_merit),
    preliminary_average_merit: formatMeritValue(row.preliminary_average_merit),
    final_lowest_merit: formatMeritValue(row.final_lowest_merit),
    final_average_merit: formatMeritValue(row.final_average_merit),
  }));
}

function drawHeaderAndFooter(
  doc,
  { pageToMunicipalities, pageWidth, pageHeight, margin }
) {
  const pageCount = doc.internal.getNumberOfPages();
  const today = new Date().toLocaleDateString("sv-SE");

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    const pageMunicipalities = pageToMunicipalities[i];
    if (pageMunicipalities && pageMunicipalities.length > 0) {
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(100, 100, 100);
      doc.text(pageMunicipalities.join(", "), pageWidth - margin, 10, {
        align: "right",
      });
      doc.setTextColor(0, 0, 0);
    }

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(`Utskrivet: ${today}`, margin, pageHeight - 10);
    doc.text(`Sida ${i} av ${pageCount}`, pageWidth - margin, pageHeight - 10, {
      align: "right",
    });
  }
}

/**
 * Generates a PDF with merit values in landscape A4 format, grouped by municipality
 * @param {Array} data - Filtered data from the table
 * @param {Object} activeFilters - Active filters (year, municipality, school, program)
 */
export function generateMeritValuesPDF(data, activeFilters) {
  // Create PDF in landscape A4 format
  const doc = new jsPDF(PDF_SETTINGS);

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = DEFAULT_MARGIN;
  const availableTableWidth = pageWidth - margin * 2;

  const scaledColumnWidths = getScaledColumnWidths(
    availableTableWidth,
    BASE_COLUMN_WIDTHS
  );

  // Header
  let yPosition = margin;
  yPosition = addMainHeader(doc, { margin, yPosition, activeFilters });

  // Group data by municipality
  const groupedData = groupByMunicipality(data);
  const municipalities = Object.keys(groupedData).sort();

  const { pageToMunicipalities, addMunicipalityToPage } =
    createPageMunicipalityTracker();

  // Generate table for each municipality
  municipalities.forEach((municipality, index) => {
    const municipalityData = groupedData[municipality];

    // Check if we need a new page (leave space for header + at least a few rows)
    if (yPosition > pageHeight - MIN_PAGE_REMAINING_SPACE && index > 0) {
      doc.addPage();
      yPosition = margin;
    }

    // Track municipality for current page
    const currentPage = doc.internal.getNumberOfPages();
    addMunicipalityToPage(currentPage, municipality);

    // Municipality header
    yPosition = drawMunicipalityHeader(doc, {
      municipality,
      margin,
      yPosition,
    });

    // Format table data for this municipality
    const tableData = mapMeritTableData(municipalityData);

    // Generate table for this municipality
    autoTable(doc, {
      startY: yPosition,
      head: [TABLE_COLUMNS.map((col) => col.header)],
      body: tableData.map((row) =>
        TABLE_COLUMNS.map((col) => row[col.dataKey])
      ),
      margin: { left: margin, right: margin },
      tableWidth: availableTableWidth,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [80, 80, 80], // Dark gray
        textColor: 255,
        fontStyle: "normal",
        fontSize: 8,
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245], // Light gray
      },
      columnStyles: {
        0: { cellWidth: scaledColumnWidths[0] }, // School
        1: { cellWidth: scaledColumnWidths[1] }, // Program
        2: { cellWidth: scaledColumnWidths[2] }, // Remark
        3: { cellWidth: scaledColumnWidths[3], halign: "center" }, // Preliminary lowest
        4: { cellWidth: scaledColumnWidths[4], halign: "center" }, // Preliminary average
        5: { cellWidth: scaledColumnWidths[5], halign: "center" }, // Final lowest
        6: { cellWidth: scaledColumnWidths[6], halign: "center" }, // Final average
      },
      didDrawPage: () => {
        // Track municipality for new pages created by table overflow
        const pageNum = doc.internal.getNumberOfPages();
        addMunicipalityToPage(pageNum, municipality);
      },
    });

    // Update yPosition for next municipality
    yPosition = doc.lastAutoTable.finalY + 10;
  });

  drawHeaderAndFooter(doc, {
    pageToMunicipalities,
    pageWidth,
    pageHeight,
    margin,
  });

  // Open PDF in new tab
  const pdfBlob = doc.output("blob");
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, "_blank");
}

/**
 * Groups data by municipality
 */
function groupByMunicipality(data) {
  return data.reduce((groups, row) => {
    const municipality = row.municipality || "Okänd kommun";
    if (!groups[municipality]) {
      groups[municipality] = [];
    }
    groups[municipality].push(row);
    return groups;
  }, {});
}

/**
 * Formats merit value for display
 */
function formatMeritValue(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  const num = parseFloat(value);
  if (isNaN(num)) {
    return value.toString();
  }
  return num.toFixed(1);
}
